import React, { useEffect } from "react";
import styled, { css } from "styled-components";
import { PluginStore, PluginManifest } from "graylog-web-plugin/plugin";
import useHistory from "routing/useHistory";
import useLocation from "routing/useLocation";
import Routes from "security-app/routing/Routes";
import { useInvestigationDrawer } from "security-app/components/Investigations/contexts/InvestigationDrawer";
import { useGetInvestigationDetails } from "security-app/hooks/useInvestigationsAPI";
import { Icon, Spinner } from "components/common";
import ActiveInvestigationLabel from "security-app/components/Investigations/ActiveInvestigationLabel";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import { TELEMETRY_EVENT_TYPE } from "telemetry/Constants";
import AIReportButton from "security-app/components/Investigations/AIReport/AIReportButton";
import useIsInvestigationAIReportEnabled from "security-app/components/Investigations/AIReport/hooks/useIsInvestigationAIReportEnabled";
import useLicenseCheck from "license/useLicenseCheck";
import InvestigationsDetails from "security-app/components/Investigations/Details";
import Details from "./Details";
import Evidence from "./Evidence";
import Notes from "./Notes";
const zIndexHideNavigation = 1050;
const drawerWidth = 600;
const paddingSize = 16;
const Backdrop = styled.div(
  ({ $collapsed }) => css`
    position: fixed;
    display: ${$collapsed ? "none" : "flex"};
    flex-direction: row;
    justify-content: flex-end;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    overflow: hidden;

    background-color: rgba(0, 0, 0, 0.5);

    z-index: ${zIndexHideNavigation};
  `
);
const Drawer = styled.div(
  ({ $collapsed, $fullScreen, theme }) => css`
    position: fixed;
    top: ${paddingSize + 40}px;
    right: ${$collapsed ? -drawerWidth : paddingSize}px;
    bottom: ${paddingSize}px;
    ${$fullScreen ? `width: calc(100vw - ${paddingSize * 2}px)` : `width: ${drawerWidth}px`};
    font-size: 0.85rem;
    border-radius: ${paddingSize}px;

    display: flex;
    flex-direction: column;

    background-color: ${theme.colors.global.background};

    transition: all 0.3s ease-in-out;

    z-index: ${zIndexHideNavigation};
  `
);
const DrawerContainer = styled.div`
  padding: 1rem;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  justify-content: stretch;
  flex-grow: 1;
`;
const controlsWidth = 24;
const Controls = styled.div(
  ({ $collapsed, theme }) => css`
    position: absolute;
    top: 16px;
    left: -${controlsWidth}px;
    width: ${controlsWidth}px;
    padding: 8px 5px;

    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 0.8rem;

    border: 1px solid ${theme.colors.input.border};
    background-color: ${theme.colors.global.background};
    border-right: none;
    border-top-left-radius: 8px;
    border-bottom-left-radius: 8px;
    cursor: pointer;

    & > svg {
      transition: all 0.1s ease-in-out;
    }

    & > .collapse-trigger {
      transform: rotate(${$collapsed ? "180" : "0"}deg);
    }

    & > .close-trigger {
      color: ${theme.colors.variant.danger};
    }
  `
);
const SectionDivider = styled.hr(
  ({ theme }) => css`
    border-color: ${theme.colors.input.border};
    margin: 16px 0;
  `
);
const HeaderDiv = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;
const HeaderButtons = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
`;
function InvestigationDrawer() {
  const {
    collapsed,
    setCollapsed,
    setShowDrawer,
    fullScreen,
    setFullScreen,
    clearInvestigation,
    selectedInvestigationId
  } = useInvestigationDrawer();
  const sendTelemetry = useSendTelemetry("investigation");
  const isAIReportFeatureEnabled = useIsInvestigationAIReportEnabled();
  const { loadingInvestigationDetails, currentInvestigation } = useGetInvestigationDetails(selectedInvestigationId);
  const {
    security: { isValid: validSecurityLicense }
  } = useLicenseCheck();
  const { push } = useHistory();
  const { pathname } = useLocation();
  useEffect(() => setShowDrawer(!!selectedInvestigationId), [setShowDrawer, selectedInvestigationId]);
  const removeLocalStorageInvestigationId = () => {
    localStorage.removeItem("currentInvestigationId");
  };
  PluginStore.register(new PluginManifest({}, { "hooks.logout": [removeLocalStorageInvestigationId] }));
  const onClose = () => {
    sendTelemetry(TELEMETRY_EVENT_TYPE.SECURITY_APP.INVESTIGATION_DRAWER_CLOSED, {
      app_pathname: "security"
    });
    clearInvestigation();
    if (pathname.includes(`investigations/${selectedInvestigationId}`)) push(Routes.INVESTIGATIONS.LIST);
  };
  const onCollapseToggle = () => {
    sendTelemetry(TELEMETRY_EVENT_TYPE.SECURITY_APP.INVESTIGATION_DRAWER_COLLAPSE_TOGGLED, {
      app_pathname: "security",
      event_details: {
        collapsed: !collapsed
      }
    });
    setCollapsed(!collapsed);
    setFullScreen(false);
  };
  const onFullScreenToggle = () => {
    setFullScreen(!fullScreen);
  };
  const controlsRenderer = () => {
    if (!validSecurityLicense)
      return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(
        Icon,
        {
          className: "collapse-trigger",
          name: "keyboard_tab",
          title: "Expand details",
          onClick: () => onCollapseToggle()
        }
      ), /* @__PURE__ */ React.createElement(Icon, { className: "close-trigger", name: "close", title: "close drawer", onClick: onClose }));
    if (collapsed)
      return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(
        Icon,
        {
          className: "collapse-trigger",
          name: "keyboard_tab",
          title: "Expand details",
          onClick: () => onCollapseToggle()
        }
      ), /* @__PURE__ */ React.createElement(Icon, { className: "close-trigger", name: "close", title: "close drawer", onClick: onClose }));
    if (!fullScreen && !collapsed)
      return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(
        Icon,
        {
          className: "fullscreen-trigger",
          name: "keyboard_tab_rtl",
          title: "Toggle fullscreen",
          onClick: () => onFullScreenToggle()
        }
      ), /* @__PURE__ */ React.createElement(
        Icon,
        {
          className: "collapse-trigger",
          name: "keyboard_tab",
          title: "Collapse details",
          onClick: () => onCollapseToggle()
        }
      ));
    return null;
  };
  const renderDrawerContent = () => {
    if (fullScreen && validSecurityLicense) {
      return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(HeaderDiv, null, /* @__PURE__ */ React.createElement(HeaderButtons, null, /* @__PURE__ */ React.createElement(
        Icon,
        {
          className: "fullscreen-trigger",
          name: "keyboard_tab",
          title: "Toggle fullscreen",
          onClick: () => onFullScreenToggle()
        }
      )), /* @__PURE__ */ React.createElement(HeaderButtons, null, /* @__PURE__ */ React.createElement(Icon, { className: "close-trigger", name: "close", title: "close drawer", onClick: onClose }))), /* @__PURE__ */ React.createElement(SectionDivider, null), /* @__PURE__ */ React.createElement(InvestigationsDetails, { investigation: currentInvestigation, inModal: true }));
    }
    return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(HeaderDiv, null, /* @__PURE__ */ React.createElement("h3", null, "Investigation Details", " ", (currentInvestigation == null ? void 0 : currentInvestigation.id) === selectedInvestigationId && /* @__PURE__ */ React.createElement(ActiveInvestigationLabel, null, "Active")), /* @__PURE__ */ React.createElement(HeaderButtons, null, isAIReportFeatureEnabled && /* @__PURE__ */ React.createElement(AIReportButton, { investigation: currentInvestigation, small: true, logsCount: currentInvestigation.logs_count }), /* @__PURE__ */ React.createElement("div", { style: { marginLeft: "0.5rem" } }, /* @__PURE__ */ React.createElement(Icon, { className: "close-trigger", name: "close", title: "close drawer", onClick: onClose })))), /* @__PURE__ */ React.createElement(SectionDivider, null), /* @__PURE__ */ React.createElement(Details, { investigation: currentInvestigation }), /* @__PURE__ */ React.createElement(SectionDivider, null), /* @__PURE__ */ React.createElement(Evidence, { investigation: currentInvestigation }), /* @__PURE__ */ React.createElement(SectionDivider, null), /* @__PURE__ */ React.createElement(Notes, { investigation: currentInvestigation }));
  };
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(Backdrop, { $collapsed: collapsed, onClick: () => onCollapseToggle() }), /* @__PURE__ */ React.createElement(Drawer, { $collapsed: collapsed, $fullScreen: fullScreen }, !fullScreen && /* @__PURE__ */ React.createElement(Controls, { $collapsed: collapsed }, controlsRenderer()), loadingInvestigationDetails || !currentInvestigation ? /* @__PURE__ */ React.createElement(Spinner, { text: "Loading investigation details..." }) : /* @__PURE__ */ React.createElement(DrawerContainer, { style: { overflowY: "auto" } }, renderDrawerContent())));
}
export default InvestigationDrawer;
